import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/base/Toast";
import { useStore } from "@/context/StoreContext";
import { formatSAR } from "@/utils/format";
import type { PaymentMethod } from "@/types";
import SettlementSummary from "../components/SettlementSummary";

type PayMethod = Extract<PaymentMethod, "card" | "applepay">;
type PayMode = "full" | "partial";

const round2 = (value: number): number => Math.round(value * 100) / 100;

const METHOD_OPTIONS: {
  key: PayMethod;
  label: string;
  icon: string;
}[] = [
  { key: "card", label: "بطاقة", icon: "ri-bank-card-line" },
  { key: "applepay", label: "Apple Pay", icon: "ri-apple-fill" },
];

// Simulated card / Apple Pay processing. A real gateway call is wired here later.
const processPayment = (): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, 1200);
  });

export default function SettlePage() {
  const navigate = useNavigate();
  const { show } = useToast();
  const { cashDebt, settleDebt } = useStore();

  const [mode, setMode] = useState<PayMode>("full");
  const [amountInput, setAmountInput] = useState("");
  const [method, setMethod] = useState<PayMethod>("card");
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);
  const [failure, setFailure] = useState("");
  const [success, setSuccess] = useState<{
    paid: number;
    remaining: number;
  } | null>(null);

  const requestedAmount =
    mode === "full" ? round2(cashDebt) : Number(amountInput) || 0;
  const remaining = Math.max(round2(cashDebt - requestedAmount), 0);
  const methodLabel =
    METHOD_OPTIONS.find((item) => item.key === method)?.label ?? "بطاقة";

  const isAmountInvalid = mode === "partial" && requestedAmount <= 0;

  const pay = async () => {
    const value = round2(requestedAmount);
    if (!value || value <= 0) {
      setError("يجب أن يكون مبلغ السداد أكبر من صفر");
      return;
    }
    if (value > cashDebt + 0.001) {
      setError("لا يجوز أن يتجاوز مبلغ السداد إجمالي رسوم التطبيق المستحقة لطلبات الكاش");
      return;
    }

    setError("");
    setFailure("");
    setProcessing(true);
    try {
      await processPayment();
      const newRemaining = settleDebt(value, method);
      setSuccess({ paid: value, remaining: newRemaining });
      show("تم سداد رسوم التطبيق المستحقة لطلبات الكاش بنجاح");
    } catch {
      setFailure("تعذّر إتمام عملية الدفع، يرجى التأكد من وسيلة الدفع والمحاولة مرة أخرى.");
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    void pay();
  };

  const handleSettleRemaining = (rest: number) => {
    setSuccess(null);
    setFailure("");
    setError("");
    setMode("partial");
    setAmountInput(String(rest));
  };

  if (success) {
    const fullySettled = success.remaining <= 0.001;
    return (
      <div className="space-y-4">
        <div className="yc-fade-up flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/financial")}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-background-200 text-foreground-700 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
            aria-label="رجوع"
          >
            <i className="ri-arrow-right-line text-xl"></i>
          </button>
          <h2 className="text-xl font-bold text-foreground-950">
            سداد رسوم التطبيق المستحقة لطلبات الكاش
          </h2>
        </div>

        <section className="yc-fade-up bg-white rounded-card border border-primary-200 shadow-card p-6 text-center">
          <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 mb-4">
            <i className="ri-checkbox-circle-line text-3xl"></i>
          </div>
          <h3 className="text-lg font-bold text-foreground-950">
            تم تنفيذ عملية السداد بنجاح
          </h3>
          <p className="mt-1 text-sm text-foreground-500">
            {fullySettled
              ? "تم سداد كامل رسوم التطبيق المستحقة لطلبات الكاش وإعادة تفعيل استقبال الطلبات النقدية."
              : "تم خصم المبلغ المسدّد من رسوم التطبيق المستحقة لطلبات الكاش."}
          </p>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg mx-auto text-right">
            <div className="rounded-btn bg-background-50 border border-background-200 px-4 py-3">
              <p className="text-xs text-foreground-500">المبلغ المسدَّد</p>
              <p className="mt-1 text-lg font-bold text-foreground-950">
                {formatSAR(success.paid)}
              </p>
            </div>
            <div className="rounded-btn bg-background-50 border border-background-200 px-4 py-3">
              <p className="text-xs text-foreground-500">
                المبلغ المتبقي بعد السداد
              </p>
              <p className="mt-1 text-lg font-bold text-foreground-950">
                {formatSAR(success.remaining)}
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/financial")}
              className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-btn bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-right-line"></i>
              العودة للمعاملات المالية
            </button>
            {!fullySettled && (
              <button
                type="button"
                onClick={() => handleSettleRemaining(success.remaining)}
                className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-btn bg-background-100 text-foreground-700 text-sm font-semibold hover:bg-background-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-hand-coin-line"></i>
                سداد المبلغ المتبقي
              </button>
            )}
          </div>
        </section>
      </div>
    );
  }

  if (cashDebt <= 0) {
    return (
      <div className="yc-fade-up bg-white rounded-card border border-background-200 shadow-card px-8 py-20 text-center">
        <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 mb-4">
          <i className="ri-shield-check-line text-3xl"></i>
        </div>
        <h2 className="text-lg font-bold text-foreground-950">
          لا توجد رسوم مستحقة
        </h2>
        <p className="mt-1 text-sm text-foreground-500">
          حسابك في وضع سليم ولا يحتاج إلى سداد حاليًا.
        </p>
        <button
          type="button"
          onClick={() => navigate("/financial")}
          className="mt-5 inline-flex items-center gap-2 rounded-btn bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-right-line"></i>
          العودة للمعاملات المالية
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="yc-fade-up flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate("/financial")}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-background-200 text-foreground-700 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
          aria-label="رجوع"
        >
          <i className="ri-arrow-right-line text-xl"></i>
        </button>
        <div>
          <h2 className="text-xl font-bold text-foreground-950">
            سداد رسوم التطبيق المستحقة لطلبات الكاش
          </h2>
          <p className="mt-1 text-sm text-foreground-500">
            حدد مبلغ السداد واختر وسيلة الدفع لإتمام سداد رسوم التطبيق المستحقة
        لطلبات الكاش.
          </p>
        </div>
      </div>

      <SettlementSummary
        cashDebt={cashDebt}
        amount={requestedAmount}
        remaining={remaining}
        methodLabel={methodLabel}
      />

      <form
        onSubmit={handleSubmit}
        className="yc-fade-up bg-white rounded-card border border-background-200 shadow-card p-5 md:p-6"
      >
        <div className="max-w-xl space-y-6">
          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-2">
              طريقة السداد
            </label>
            <div className="inline-flex items-center gap-1 rounded-full bg-background-100 p-1">
              <button
                type="button"
                onClick={() => setMode("full")}
                className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                  mode === "full"
                    ? "bg-white text-foreground-950 shadow-card"
                    : "text-foreground-500 hover:text-foreground-800"
                }`}
              >
                سداد كامل المبلغ
              </button>
              <button
                type="button"
                onClick={() => setMode("partial")}
                className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                  mode === "partial"
                    ? "bg-white text-foreground-950 shadow-card"
                    : "text-foreground-500 hover:text-foreground-800"
                }`}
              >
                سداد مبلغ جزئي
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-2">
              المبلغ المراد سداده
            </label>
            <input
              type="number"
              min="0"
              step="0.5"
              inputMode="decimal"
              disabled={mode === "full"}
              value={mode === "full" ? cashDebt.toFixed(2) : amountInput}
              onChange={(event) => {
                setAmountInput(event.target.value);
                setError("");
              }}
              placeholder="0.00"
              className={`w-full h-11 rounded-btn border border-background-200 px-3 text-sm outline-none focus:ring-2 focus:ring-primary-300 ${
                mode === "full"
                  ? "bg-background-50 text-foreground-500 cursor-not-allowed"
                  : ""
              }`}
            />
            {error ? (
              <p className="mt-1.5 flex items-center gap-1.5 text-xs text-red-500">
                <i className="ri-error-warning-line"></i>
                {error}
              </p>
            ) : isAmountInvalid ? (
              <p className="mt-1.5 flex items-center gap-1.5 text-xs text-foreground-500">
                <i className="ri-information-line"></i>
                المبلغ المتبقي بعد السداد يُحدَّث تلقائيًا عند تغييره.
              </p>
            ) : (
              <p className="mt-1.5 flex items-center gap-1.5 text-xs text-foreground-500">
                <i className="ri-information-line"></i>
                المبلغ المتبقي بعد السداد: {formatSAR(remaining)}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-2">
              وسيلة الدفع
            </label>
            <div className="grid grid-cols-2 gap-3 max-w-sm">
              {METHOD_OPTIONS.map((option) => {
                const active = method === option.key;
                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => {
                      setMethod(option.key);
                      setFailure("");
                    }}
                    className={`flex items-center gap-3 rounded-btn border px-4 py-3 text-sm font-semibold transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                      active
                        ? "border-primary-500 bg-primary-50 text-primary-800"
                        : "border-background-200 bg-white text-foreground-600 hover:border-background-400"
                    }`}
                  >
                    <span
                      className={`w-8 h-8 flex items-center justify-center rounded-full ${
                        active
                          ? "bg-primary-100 text-primary-700"
                          : "bg-background-100 text-foreground-500"
                      }`}
                    >
                      <i className={`${option.icon} text-lg`}></i>
                    </span>
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {failure && (
          <div className="mt-6 flex flex-col gap-3 rounded-btn border border-red-200 bg-red-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="flex items-center gap-2 text-sm font-medium text-red-600">
              <i className="ri-error-warning-line"></i>
              {failure}
            </p>
            <button
              type="button"
              onClick={() => void pay()}
              className="inline-flex items-center justify-center gap-2 h-10 px-5 rounded-btn bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors duration-200 cursor-pointer whitespace-nowrap shrink-0"
            >
              <i className="ri-refresh-line"></i>
              إعادة المحاولة
            </button>
          </div>
        )}

        <div className="mt-6 flex gap-3 border-t border-background-100 pt-5">
          <button
            type="button"
            onClick={() => navigate("/financial")}
            className="h-11 px-6 rounded-btn bg-background-100 text-foreground-700 text-sm font-semibold hover:bg-background-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            إلغاء
          </button>
          <button
            type="submit"
            disabled={processing}
            className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-btn bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {processing ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                جارٍ تنفيذ الدفع...
              </>
            ) : (
              <>
                <i className="ri-secure-payment-line"></i>
                تأكيد السداد
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}